/**
 * Ordenação dos tipos de conteúdo do menu "Adicionar conteúdo".
 *
 * O backend devolve `@types` em ordem alfabética por título, o que joga os
 * tipos de uso diário (Pasta, Página, Notícia) para o meio da lista. Aqui a
 * ordem é definida por `portal_type` — chave estável, imune a mudança de
 * título ou tradução.
 */

export type ContentTypeItem = {
  '@id': string;
  title?: string;
  addable?: boolean;
};

/**
 * Extrai o `portal_type` do `@id` do item (`.../@types/Folder` -> `Folder`).
 */
export const getPortalType = (item: ContentTypeItem): string =>
  item?.['@id']?.split('@types/')[1] ?? '';

/**
 * Reordena (sem nunca filtrar) os tipos segundo `order`.
 *
 * Tipos fora de `order` vão para o fim, ordenados por título em pt-BR — assim
 * um tipo novo aparece no menu mesmo que ninguém atualize a lista.
 */
export const sortContentTypes = <T extends ContentTypeItem>(
  types: T[],
  order: string[] = [],
): T[] => {
  if (!Array.isArray(types)) return types;

  const rank = (item: T) => {
    const index = order.indexOf(getPortalType(item));
    return index === -1 ? Number.POSITIVE_INFINITY : index;
  };

  return [...types].sort((a, b) => {
    const diff = rank(a) - rank(b);
    if (!Number.isNaN(diff) && diff !== 0) return diff;
    return (a.title ?? '').localeCompare(b.title ?? '', 'pt-BR');
  });
};

export default sortContentTypes;
